import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GearTabs from "@/components/gear/GearTabs";
import KiteBrands from "@/components/gear/KiteBrands";
import KiteModels from "@/components/gear/KiteModels";
import KiteVersions from "@/components/gear/KiteVersions";

const GearPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("brands");
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(5);

  // Get tab, search, page, and limit from URL when component mounts or URL changes
  useEffect(() => {
    // Get tab, search, page, and limit from URL using URLSearchParams
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get("tab");
    const searchFromUrl = searchParams.get("searchval");
    const pageFromUrl = searchParams.get("page");
    const limitFromUrl = searchParams.get("limit");

    if (tabFromUrl && ["brands", "models", "versions"].includes(tabFromUrl)) {
      setSelectedTab(tabFromUrl);
    }

    if (searchFromUrl) {
      setSearchValue(searchFromUrl);
    } else {
      setSearchValue("");
    }

    if (pageFromUrl && !isNaN(parseInt(pageFromUrl))) {
      setCurrentPage(parseInt(pageFromUrl));
    } else {
      setCurrentPage(1);
    }

    // Valid limit values: 2, 5, 10, 20, 50
    const validLimits = [2, 5, 10, 20, 50];
    if (limitFromUrl && !isNaN(parseInt(limitFromUrl))) {
      const parsedLimit = parseInt(limitFromUrl);
      if (validLimits.includes(parsedLimit)) {
        setCurrentLimit(parsedLimit);
      }
    }
  }, [location.search]);

  // Update URL when tab changes
  const handleTabSelect = (tab: string) => {
    setSelectedTab(tab);
    // When changing tabs, reset to page 1
    setCurrentPage(1);
    updateUrlParams({ tab, page: null });
  };

  // Update URL when search changes
  const handleSearchChange = (search: string) => {
    setSearchValue(search);
    // Reset to page 1 when changing search
    setCurrentPage(1);
    updateUrlParams({ searchval: search || null, page: null }); // Remove params if empty
  };

  // Update URL when page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams({ page: page > 1 ? page.toString() : null }); // Remove page param if on page 1
  };

  // Update URL when limit changes
  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(1); // Reset to page 1 when changing limit
    updateUrlParams({
      limit: limit !== 5 ? limit.toString() : null, // Remove limit param if default (5)
      page: null, // Reset page to 1
    });
  };

  // Helper to update URL params
  const updateUrlParams = (params: { [key: string]: string | null }) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === "") {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });

    // Update URL without causing a navigation/reload
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const renderContent = () => {
    switch (selectedTab) {
      case "brands":
        return (
          <KiteBrands
            initialSearch={searchValue}
            onSearchChange={handleSearchChange}
            initialPage={currentPage}
            onPageChange={handlePageChange}
            initialLimit={currentLimit}
            onLimitChange={handleLimitChange}
          />
        );
      case "models":
        return (
          <KiteModels
            initialSearch={searchValue}
            onSearchChange={handleSearchChange}
            initialPage={currentPage}
            onPageChange={handlePageChange}
            initialLimit={currentLimit}
            onLimitChange={handleLimitChange}
          />
        );
      case "versions":
        return (
          <KiteVersions
            initialSearch={searchValue}
            onSearchChange={handleSearchChange}
            initialPage={currentPage}
            onPageChange={handlePageChange}
            initialLimit={currentLimit}
            onLimitChange={handleLimitChange}
          />
        );
      default:
        return (
          <KiteBrands
            initialSearch={searchValue}
            onSearchChange={handleSearchChange}
            initialPage={currentPage}
            onPageChange={handlePageChange}
            initialLimit={currentLimit}
            onLimitChange={handleLimitChange}
          />
        );
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Gear Management</h1>
      <GearTabs selectedTab={selectedTab} onSelect={handleTabSelect} />
      {renderContent()}
    </div>
  );
};

export default GearPage;
